import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../store/userStore";
import {
  fetchAgents,
  getAgentGoogleAuthUrl,
  getAgentGoogleStatus,
  disconnectAgentGoogle,
  type Agent,
  type AgentGoogleStatus,
} from "../../Layouts/crm/services/agentsService";

// ===========================================================================
// Mi Google Calendar. Las reuniones y citas del CRM se escriben en el
// calendario del AGENTE (vendedor) que las atiende: aquí cada persona conecta el
// suyo. El agente se reconoce por el correo de la sesión; quien no es vendedor
// del CRM no tiene nada que conectar y se le dice.
// ===========================================================================

type Phase = "loading" | "no-agent" | "ready" | "error";

/** El vendedor del CRM de esta persona: mismo correo, sin importar mayúsculas. */
export function findMyAgent(agents: Agent[], email: string | null | undefined): Agent | null {
  const mine = String(email || "").trim().toLowerCase();
  if (!mine) return null;
  return agents.find((a) => a.type === "human" && String(a.email || "").trim().toLowerCase() === mine) ?? null;
}

/** El mensaje (ya traducido) de un 403 de la API, si lo trae. */
function serverMessage(e: unknown): string | null {
  const res = (e as { response?: { status?: number; data?: { meta?: { message?: string }; message?: string } } }).response;
  if (res?.status !== 403) return null;
  return res.data?.meta?.message || res.data?.message || null;
}

export default function GoogleCalendarIntegration({ isDarkMode }: { isDarkMode: boolean }) {
  const { t } = useTranslation("common");
  const email = useUserStore((state) => state.user?.email);
  const [phase, setPhase] = useState<Phase>("loading");
  const [agent, setAgent] = useState<Agent | null>(null);
  const [status, setStatus] = useState<AgentGoogleStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const poll = useRef<number | null>(null);

  const stopPolling = () => {
    if (poll.current !== null) window.clearInterval(poll.current);
    poll.current = null;
  };

  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const page = await fetchAgents({ type: "human", limit: 300 });
      const mine = findMyAgent(page.data, email);
      setAgent(mine);
      if (!mine) return setPhase("no-agent");
      setStatus(await getAgentGoogleStatus(mine.id));
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [email]);

  useEffect(() => {
    load();
    return stopPolling;
  }, [load]);

  const connect = async () => {
    if (!agent) return;
    setBusy(true);
    setNotice(null);
    try {
      const url = await getAgentGoogleAuthUrl(agent.id);
      if (!url) throw new Error("sin-url");
      const popup = window.open(url, "crm-google-oauth", "width=520,height=680");
      if (!popup) {
        setNotice(t("profile.integrations.google.popupBlocked", "Allow pop-ups for this site and try again."));
        setBusy(false);
        return;
      }
      const finish = async () => {
        stopPolling();
        window.removeEventListener("message", onMessage);
        const next = await getAgentGoogleStatus(agent.id).catch(() => null);
        if (next) setStatus(next);
        setBusy(false);
      };
      const onMessage = (ev: MessageEvent) => {
        if (ev.data && ev.data.source === "crm-google-oauth") finish();
      };
      window.addEventListener("message", onMessage);
      // Respaldo por si el aviso de la ventana no llega: se consulta el estado.
      poll.current = window.setInterval(async () => {
        const next = await getAgentGoogleStatus(agent.id).catch(() => null);
        if (next?.connected || popup.closed) {
          if (next) setStatus(next);
          finish();
        }
      }, 2500);
    } catch (e) {
      // Si la API lo rechaza (p. ej. el correo de la cuenta no está verificado y
      // por eso no reclama a este vendedor), se muestra su explicación.
      setNotice(serverMessage(e) || t("profile.integrations.google.connectError", "Could not start the Google connection. Try again."));
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!agent) return;
    setBusy(true);
    setNotice(null);
    try {
      await disconnectAgentGoogle(agent.id);
      setStatus(await getAgentGoogleStatus(agent.id));
    } catch (e) {
      setNotice(serverMessage(e) || t("profile.integrations.google.disconnectError", "Could not disconnect. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const card = isDarkMode ? "border-[#333] bg-[#1c1c1c]" : "border-gray-200 bg-white";
  const connected = Boolean(status?.connected);
  const configured = status?.configured !== false && status?.oauthConfigured !== false;

  return (
    <div data-testid="integration-google-calendar" className={`rounded-xl border p-4 ${card}`}>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/15">
          <i className="ri-calendar-event-line text-lg text-blue-500" />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium ${text}`}>{t("profile.integrations.google.name", "Google Calendar")}</p>
          <p className={`text-xs ${muted}`}>
            {t("profile.integrations.google.description", "Meetings and bookings assigned to you are written to your calendar.")}
          </p>
          {phase === "ready" && connected && status?.email && (
            <p className={`mt-1 text-xs ${muted}`}>
              {t("profile.integrations.google.connectedAs", "Connected as")} <span className={text}>{status.email}</span>
            </p>
          )}
        </div>

        {phase === "loading" && <i className={`ri-loader-4-line animate-spin ${muted}`} />}
        {phase === "ready" && (
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                connected ? "bg-emerald-500/15 text-emerald-500" : "bg-gray-400/15 text-gray-500"
              }`}
            >
              {connected ? t("profile.integrations.connected", "Connected") : t("profile.integrations.notConnected", "Not connected")}
            </span>
            {connected ? (
              <button
                onClick={disconnect}
                disabled={busy}
                className="rounded-lg border border-red-500/50 px-3 py-1.5 text-sm text-red-500 hover:bg-red-500/10 disabled:opacity-50"
              >
                {t("profile.integrations.disconnect", "Disconnect")}
              </button>
            ) : (
              <button
                onClick={connect}
                disabled={busy || !configured}
                className="rounded-lg bg-primary-500 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-primary-400 disabled:opacity-50"
              >
                {busy ? <i className="ri-loader-4-line animate-spin" /> : t("profile.integrations.connect", "Connect")}
              </button>
            )}
          </div>
        )}
        {phase === "error" && (
          <button onClick={load} className={`rounded-lg border px-3 py-1.5 text-sm ${isDarkMode ? "border-[#444]" : "border-gray-300"} ${text}`}>
            {t("profile.integrations.retry", "Retry")}
          </button>
        )}
      </div>

      {phase === "no-agent" && (
        <p className={`mt-3 text-xs ${muted}`}>
          <i className="ri-information-line mr-1" />
          {t(
            "profile.integrations.google.noAgent",
            "Your account is not a CRM sales agent yet. An administrator can add you under CRM → Settings → Agents with this same email.",
          )}
        </p>
      )}
      {phase === "ready" && !configured && (
        <p className={`mt-3 text-xs ${muted}`}>
          <i className="ri-information-line mr-1" />
          {t("profile.integrations.google.notConfigured", "The Google connection is not set up on this platform yet.")}
        </p>
      )}
      {phase === "error" && (
        <p className="mt-3 text-xs text-red-500">{t("profile.integrations.loadError", "Could not load this integration.")}</p>
      )}
      {notice && <p className="mt-3 text-xs text-amber-500">{notice}</p>}
    </div>
  );
}
